/* -------------------------------------------------------------------------- */
/*                        Componente TimeRangeInput                           */
/* -------------------------------------------------------------------------- */

import { Label, FormGroup, Row, Col } from 'reactstrap';
import moment from 'moment';
import Flatpickr from 'react-flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es';
import { english } from 'flatpickr/dist/l10n/default';
import { FormattedMessage } from 'react-intl';
import { appIntl } from '../../../utility/context/IntlGlobalProvider';
import { XSquare } from 'react-feather';
import { createDateWithHoursAndMinutes } from 'utility/Utils';
import { useEffect, useRef } from 'react';

interface Props {
  field: any;
  fieldstate: any;
  errorMessage?: any;
  required?: boolean;
  defaultHour?: number;
  defaultMinute?: number;
  maxTime?: any;
  minTime?: any;
  label?: any;
  disabled?: boolean;
  allowNulls?: boolean;
  inFormat?: boolean;
}

const TimeInput = ({
  field,
  fieldstate,
  required = false,
  maxTime,
  errorMessage,
  minTime,
  label,
  disabled,
  allowNulls = false,
  inFormat = false,
  defaultHour = 0,
  defaultMinute = 0
}: Props) => {
  const flatpickrRef = useRef<any>(null);

  /* Normaliza cuando viene como string HH:mm (modo edición) */
  useEffect(() => {
    if (inFormat && typeof field.value === 'string' && field.value) {
      field.onChange(moment(field.value, 'HH:mm').toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormGroup>
      <Label for={field.name}>
        {label || <FormattedMessage id="common.time" />}
        {required && <span className="required">&nbsp;*</span>}
      </Label>

      <Row>
        <Col sm={allowNulls ? '10' : undefined}>
          <Flatpickr
            ref={flatpickrRef}
            id={field.name}
            disabled={disabled}
            className={
              fieldstate.error
                ? 'form-control flatpickr-input has-error'
                : 'form-control flatpickr-input'
            }
            /* '' mantiene el input en blanco cuando no hay valor */
            value={field.value ?? ''}
            options={{
              locale: appIntl().locale === 'es' ? Spanish : english,
              enableTime: true,
              noCalendar: true,
              dateFormat: 'H:i',
              defaultHour,
              defaultMinute,
              time_24hr: true,
              minTime,
              maxTime
            }}
            onChange={(dates) => {
              const [dateValue] = dates;
              if (dateValue) {
                field.onChange(
                  inFormat ? moment(dateValue).format('HH:mm') : dateValue
                );
              }
            }}
            onOpen={() => {
              /* Si no hay valor, inicializa en 00:00 y dispara onChange */
              if (!field.value && flatpickrRef.current?.flatpickr) {
                const midnight = createDateWithHoursAndMinutes(0, 0);
                flatpickrRef.current.flatpickr.setDate(midnight, true); // <- true!
              }
            }}
            onClose={() => {
              /* Si el picker quedó sin selección, limpia el campo */
              const fp = flatpickrRef.current?.flatpickr;
              if (!fp?.selectedDates.length) {
                field.onChange(null);
              }
            }}
          />
        </Col>

        {allowNulls && (
          <Col sm="1" className="mt-50" style={{ cursor: 'pointer' }}>
            <XSquare onClick={() => field.onChange(null)} />
          </Col>
        )}
      </Row>

      {fieldstate.error && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {fieldstate.error?.message ||
            errorMessage ||
            <FormattedMessage id="common.requiredField" />}
        </div>
      )}
    </FormGroup>
  );
};

TimeInput.defaultProps = {
  required: false,
  disabled: false
};

export default TimeInput;